import React from 'react'
import {
  LogoPlacement,
  MaterialProperties,
  MaterialPreset,
  MaterialSide,
  ModelUnit,
  LengthUnits,
  MODEL_UNIT_LABEL,
  lengthStep,
  materialPresets,
  roundLength,
} from '../types'
import { LogoPanel } from './LogoPanel'
import { Checkbox, Field, NumberInput, Section, Select, Slider, TextInput } from './ui'

interface MaterialSettingsPanelProps {
  material: MaterialProperties
  onChange: (material: MaterialProperties) => void
  /** All part ids, for the pattern's "align to a part" reference picker. */
  objectIds?: string[]
  /** Geometry center of the selected part (model units) — the position shown at zero offset. */
  positionBase?: [number, number, number]
  /** Current move offset of the selected part (model units). */
  positionOffset?: [number, number, number]
  /** Called with the new offset to move the selected part. Absent when no single part is selected. */
  onPositionChange?: (offset: [number, number, number]) => void
  /** What an edit here will affect — a part name, "N selected parts", or "All parts". */
  scopeLabel?: string
  /** Hand the selected parts back to the shared material. Absent when they already follow it. */
  onResetMaterial?: () => void
  /** Unit every length is shown in, and the factor from model units into it. */
  units?: LengthUnits
  /** Pick a different display unit. Converts the readouts; never rescales the model. */
  onDisplayUnitChange?: (unit: ModelUnit) => void
  /** Overall model size in model units. */
  modelSize?: [number, number, number] | null
  /**
   * The selected parts arrived with their own authored PBR maps. The pattern controls are
   * hidden: they wrap artwork onto a generated cylindrical UV space, and these parts carry
   * a hand-made atlas instead, so a pattern would be laid out against coordinates that do
   * not describe them. Artwork still goes on such a part via the Logo tool, which builds
   * its own sheet with its own UVs.
   */
  hasAuthoredMaterial?: boolean
  /**
   * Present when the selected part is a generated logo. It replaces the generic
   * Position block (a logo is placed by angle/height on the surface, not by XYZ) and
   * hides the pattern controls, which belong to the body it sits on.
   */
  logoControls?: {
    placement: LogoPlacement
    onChange: (placement: LogoPlacement) => void
    name: string
    onRename: (name: string) => void
    targetIds: string[]
    pickMode: boolean
    onTogglePick: () => void
    onFace: () => void
    wrapDeg: number
  }
}

const presetOptions: { label: string; value: MaterialPreset }[] = [
  { label: 'Default', value: 'default' },
  { label: 'Powder coat (matte)', value: 'powder' },
  { label: 'Acrylic', value: 'acrylic' },
  { label: 'Glass', value: 'glass' },
  { label: 'Steel', value: 'steel' },
  { label: 'Custom', value: 'custom' },
]

const sideOptions: { label: string; value: MaterialSide }[] = [
  { label: 'Front', value: 'front' },
  { label: 'Back', value: 'back' },
  { label: 'Double', value: 'double' },
]

export const MaterialSettingsPanel: React.FC<MaterialSettingsPanelProps> = ({
  material,
  onChange,
  objectIds,
  positionBase,
  positionOffset,
  onPositionChange,
  logoControls,
  scopeLabel,
  onResetMaterial,
  units: unitsProp,
  onDisplayUnitChange,
  modelSize,
  hasAuthoredMaterial,
}) => {
  const units: LengthUnits = unitsProp ?? { unit: 'mm', scale: 1 }
  const { unit, scale } = units

  // The fields show absolute position in the display unit. Editing sets the offset (model
  // units) so the part moves TO the typed value instead of BY it.
  const handleAxis = (axis: 0 | 1 | 2, raw: string) => {
    const value = parseFloat(raw)
    if (Number.isNaN(value) || !positionOffset || !onPositionChange) return
    const base = positionBase ?? [0, 0, 0]
    const next: [number, number, number] = [...positionOffset]
    next[axis] = value / (scale || 1) - base[axis]
    onPositionChange(next)
  }

  const axisValue = (i: number) => {
    const base = positionBase ?? [0, 0, 0]
    const offset = positionOffset ?? [0, 0, 0]
    return roundLength((base[i] + offset[i]) * scale, unit)
  }

  const handlePresetChange = (preset: MaterialPreset) => {
    // A preset changes the surface FINISH, so it keeps what the part is rather than what
    // it is made of: the artwork placed on it, and the colour. Base colours come off a
    // spec sheet — having to retype one after reaching for a finish is backwards, and it
    // is the reason picking a matte preset to fix a too-glossy black used to throw the
    // black away.
    const identity = {
      color: material.color,
      patternImage: material.patternImage,
      patternRepeatX: material.patternRepeatX,
      patternRepeatY: material.patternRepeatY,
      patternOffsetX: material.patternOffsetX,
      patternOffsetY: material.patternOffsetY,
      patternScale: material.patternScale,
    }
    if (preset === 'custom') {
      onChange({ ...material, preset: 'custom' })
    } else {
      onChange({ ...materialPresets[preset], ...identity, preset })
    }
  }

  const handleChange = <K extends keyof MaterialProperties>(key: K, value: MaterialProperties[K]) => {
    onChange({ ...material, [key]: value, preset: 'custom' })
  }

  const clamp = (v: number, min: number, max: number) => Math.min(max, Math.max(min, v))

  // Commit a typed numeric value (clamped), ignoring transient non-numeric input.
  const handleNumberInput = (key: keyof MaterialProperties, raw: string, min: number, max: number) => {
    const v = parseFloat(raw)
    if (!Number.isNaN(v)) handleChange(key, clamp(v, min, max) as MaterialProperties[typeof key])
  }

  const handlePatternFile = (file: File | undefined) => {
    if (!file) return
    const reader = new FileReader()
    reader.onload = () => {
      onChange({
        ...material,
        patternImage: reader.result as string,
        patternRepeatX: material.patternRepeatX ?? 1,
        patternRepeatY: material.patternRepeatY ?? 1,
        preset: 'custom',
      })
    }
    reader.readAsDataURL(file)
  }

  /**
   * Compact editable readout for a slider. The pattern nudges are registration
   * adjustments where a typed value beats dragging, so they keep both.
   */
  const readout = (key: keyof MaterialProperties, value: number, min: number, max: number, step: number) => (
    <input
      type="number"
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={(e) => handleNumberInput(key, e.target.value, min, max)}
      className="w-[70px] h-[22px] rounded-sm border border-gray-300 bg-white px-xs text-text-xs font-mono text-gray-700 text-right outline-none focus:border-primary-500 transition-all"
    />
  )

  /** Slider paired with a typed value, the shape most of this panel takes. */
  const range = (
    label: string,
    key: keyof MaterialProperties,
    min: number,
    max: number,
    step: number,
    hint?: React.ReactNode
  ) => (
    <Field label={label} value={(material[key] as number).toFixed(2)} hint={hint}>
      <Slider
        min={min}
        max={max}
        step={step}
        value={material[key] as number}
        onChange={(e) => handleChange(key, parseFloat(e.target.value) as MaterialProperties[typeof key])}
      />
    </Field>
  )

  return (
    <div className="w-[280px] flex-shrink-0 border-r border-gray-200 bg-white flex flex-col overflow-y-auto">
      {/* Units first: every length in this panel is shown in this one setting. */}
      {unitsProp && onDisplayUnitChange && (
        <Section title="Model">
          <Field
            label="Show sizes in"
            hint={
              modelSize
                ? `Model measures ${modelSize.map((v) => roundLength(v * scale, unit)).join(' x ')} ${unit}.`
                : undefined
            }
          >
            <Select value={unit} onChange={(e) => onDisplayUnitChange(e.target.value as ModelUnit)}>
              {(Object.keys(MODEL_UNIT_LABEL) as ModelUnit[]).map((u) => (
                <option key={u} value={u}>
                  {MODEL_UNIT_LABEL[u]} ({u})
                </option>
              ))}
            </Select>
          </Field>
        </Section>
      )}

      <div className="px-md pt-md pb-sm border-b border-gray-200">
        <h3 className="text-title-sm text-gray-900">Material</h3>
        {/* Which parts the controls below apply to — without this it is easy to recolour
            the whole model while believing one part is selected. */}
        <div className="flex items-baseline justify-between gap-sm mt-xs">
          <span className="text-text-xs text-gray-500 truncate">
            Applies to <span className="font-mono text-primary-700">{scopeLabel ?? 'All parts'}</span>
          </span>
          {onResetMaterial && (
            <button
              onClick={onResetMaterial}
              className="text-text-xs text-primary-600 hover:text-primary-800 flex-shrink-0 transition-all"
              title="Drop this part's own material so it follows the shared one again"
            >
              Use shared
            </button>
          )}
        </div>
      </div>

      <Section>
        <Field label="Preset" value={material.preset}>
          <Select value={material.preset} onChange={(e) => handlePresetChange(e.target.value as MaterialPreset)}>
            {presetOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="Base colour">
          <div className="flex gap-sm">
            <input
              type="color"
              value={material.color.slice(0, 7)}
              onChange={(e) => handleChange('color', e.target.value)}
              className="w-[44px] h-[37px] rounded-md border border-gray-300 bg-white cursor-pointer flex-shrink-0 p-[3px]"
            />
            <TextInput
              value={material.color}
              onChange={(e) => {
                const val = e.target.value
                if (val.match(/^#[0-9A-Fa-f]{0,8}$/)) handleChange('color', val)
              }}
              onBlur={(e) => {
                const val = e.target.value
                if (!val.match(/^#[0-9A-Fa-f]{6}([0-9A-Fa-f]{2})?$/)) handleChange('color', '#cccccc')
              }}
              placeholder="#ccccccff"
              className="font-mono uppercase"
            />
          </div>
        </Field>
      </Section>

      {logoControls && (
        <LogoPanel
          placement={logoControls.placement}
          onChange={logoControls.onChange}
          name={logoControls.name}
          onRename={logoControls.onRename}
          targetIds={logoControls.targetIds}
          units={units}
          pickMode={logoControls.pickMode}
          onTogglePick={logoControls.onTogglePick}
          onFace={logoControls.onFace}
          wrapDeg={logoControls.wrapDeg}
        />
      )}

      {/* Position (per-object move) */}
      {!logoControls && onPositionChange && positionOffset && (
        <Section
          title={`Position (${unit})`}
          action={
            <button
              onClick={() => onPositionChange([0, 0, 0])}
              className="text-text-xs text-primary-600 hover:text-primary-800 transition-all"
            >
              Reset
            </button>
          }
        >
          <div className="flex gap-sm">
            {(['X', 'Y', 'Z'] as const).map((ax, i) => (
              <Field key={ax} label={ax} className="flex-1">
                <NumberInput
                  step={lengthStep(unit)}
                  value={axisValue(i)}
                  onChange={(e) => handleAxis(i as 0 | 1 | 2, e.target.value)}
                />
              </Field>
            ))}
          </div>
          <p className="text-text-xs text-gray-400">
            Absolute position in {MODEL_UNIT_LABEL[unit].toLowerCase()}. Reset returns it home.
          </p>
        </Section>
      )}

      {/* A logo part carries its own artwork, so the pattern controls are hidden for it. */}
      {hasAuthoredMaterial && (
        <Section title="Pattern image">
          <p className="text-text-xs text-gray-400">
            This part came with its own textures, which are shown as authored. To add artwork, use the Logo tool.
          </p>
        </Section>
      )}

      {!logoControls && !hasAuthoredMaterial && (
        <Section title="Pattern image">
          {material.patternImage ? (
            <div className="flex flex-col gap-sm">
              <div className="flex items-center gap-sm">
                <img
                  src={material.patternImage}
                  alt="pattern"
                  className="w-[44px] h-[44px] rounded-md object-cover bg-gray-100 border border-gray-200 flex-shrink-0"
                />
                <label className="flex-1 h-[37px] flex items-center justify-center bg-white border border-gray-300 hover:border-primary-500 text-gray-700 rounded-md text-text-md cursor-pointer transition-all">
                  Replace
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => handlePatternFile(e.target.files?.[0])}
                  />
                </label>
                <button
                  onClick={() => handleChange('patternImage', undefined)}
                  className="h-[37px] px-sm rounded-md border border-error-200 bg-white text-error-700 hover:bg-error-50 text-text-md flex-shrink-0 transition-all"
                >
                  Remove
                </button>
              </div>

              <Checkbox
                checked={material.patternFill ?? false}
                onChange={(v) => handleChange('patternFill', v)}
                label="Fill entire object (stretch)"
              />

              <div className="flex gap-sm">
                <Field label={material.patternFill ? 'Repeat X' : 'Wraps around'} className="flex-1">
                  <NumberInput
                    min="1"
                    step="1"
                    value={material.patternRepeatX ?? 1}
                    onChange={(e) => handleChange('patternRepeatX', Math.max(1, parseInt(e.target.value) || 1))}
                  />
                </Field>
                {material.patternFill && (
                  <Field label="Repeat Y" className="flex-1">
                    <NumberInput
                      min="1"
                      step="1"
                      value={material.patternRepeatY ?? 1}
                      onChange={(e) => handleChange('patternRepeatY', Math.max(1, parseInt(e.target.value) || 1))}
                    />
                  </Field>
                )}
              </div>

              {!material.patternFill && (
                <>
                  <Field label="Vertical align">
                    <Select
                      value={material.patternVAlign ?? 'center'}
                      onChange={(e) =>
                        handleChange('patternVAlign', e.target.value as 'top' | 'center' | 'bottom' | 'object')
                      }
                    >
                      <option value="object">Match a part&apos;s center</option>
                      <option value="center">Object center</option>
                      <option value="top">Top</option>
                      <option value="bottom">Bottom (checkered at base)</option>
                    </Select>
                  </Field>
                  {material.patternVAlign === 'object' && (
                    <Field label="Align to part">
                      <Select
                        value={material.patternVAlignRef ?? ''}
                        onChange={(e) => handleChange('patternVAlignRef', e.target.value || undefined)}
                        className="font-mono"
                      >
                        <option value="">Logo (default)</option>
                        {(objectIds ?? []).map((id) => (
                          <option key={id} value={id}>
                            {id}
                          </option>
                        ))}
                      </Select>
                    </Field>
                  )}
                  <p className="text-text-xs text-gray-400">
                    Actual proportions kept (no stretch). &ldquo;Wraps around&rdquo; tiles it smaller; use align and
                    Raise to position it.
                  </p>
                </>
              )}

              <Field
                label="Rotate (offset X)"
                value={readout('patternOffsetX', material.patternOffsetX ?? 0, -0.5, 0.5, 0.005)}
              >
                <Slider
                  min="-0.5"
                  max="0.5"
                  step="0.005"
                  value={material.patternOffsetX ?? 0}
                  onChange={(e) => handleChange('patternOffsetX', parseFloat(e.target.value))}
                />
              </Field>
              <Field
                label="Raise (offset Y)"
                value={readout('patternOffsetY', material.patternOffsetY ?? 0, -0.5, 0.5, 0.005)}
              >
                <Slider
                  min="-0.5"
                  max="0.5"
                  step="0.005"
                  value={material.patternOffsetY ?? 0}
                  onChange={(e) => handleChange('patternOffsetY', parseFloat(e.target.value))}
                />
              </Field>
              <Field
                label="Scale"
                value={readout('patternScale', material.patternScale ?? 1, 0.25, 4, 0.01)}
                hint="Printed over the base colour; wraps with the part's UVs. Use the downloaded template to place artwork on the marked spot."
              >
                <Slider
                  min="0.25"
                  max="4"
                  step="0.01"
                  value={material.patternScale ?? 1}
                  onChange={(e) => handleChange('patternScale', parseFloat(e.target.value))}
                />
              </Field>
            </div>
          ) : (
            <label className="w-full h-[37px] flex items-center justify-center bg-white border border-dashed border-gray-300 hover:border-primary-500 text-gray-700 rounded-md text-text-md cursor-pointer transition-all">
              Upload pattern
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => handlePatternFile(e.target.files?.[0])}
              />
            </label>
          )}
        </Section>
      )}

      <Section title="Surface">
        {range('Metallic', 'metalness', 0, 1, 0.01)}
        {range('Roughness', 'roughness', 0, 1, 0.01)}
        {range(
          'Environment',
          'envMapIntensity',
          0,
          2,
          0.01,
          'How much of the surrounding studio light the surface picks up. This one is preview-only — glTF has no field for it, so an exported file is lit by whatever viewer opens it.'
        )}
        {range(
          'Specular',
          'specularIntensity',
          0,
          1,
          0.01,
          'The finish: how much of the surroundings the surface picks up. Powder coating and matte ink sit low, gloss is 1. Parts and logos set to the same value are lit the same, so a colour off a spec sheet lands the same on both.'
        )}
        {range('Transmission', 'transmission', 0, 1, 0.01, 'Glass-like transparency.')}
        {range('Thickness', 'thickness', 0, 5, 0.1, 'Volume thickness for transmission.')}
        <Field label="IOR (refraction)" value={material.ior.toFixed(2)}>
          <Slider
            min="1"
            max="2.5"
            step="0.01"
            value={material.ior}
            onChange={(e) => handleChange('ior', parseFloat(e.target.value))}
          />
          <div className="flex justify-between text-text-xs text-gray-400 mt-xs">
            <span>Water 1.33</span>
            <span>Glass 1.52</span>
            <span>Diamond 2.42</span>
          </div>
        </Field>
        {range('Alpha (opacity)', 'opacity', 0, 1, 0.01)}
      </Section>

      <Section title="Emission">
        <Field label="Colour">
          <div className="flex gap-sm">
            <input
              type="color"
              value={material.emissiveColor.slice(0, 7)}
              onChange={(e) => handleChange('emissiveColor', e.target.value)}
              className="w-[44px] h-[37px] rounded-md border border-gray-300 bg-white cursor-pointer flex-shrink-0 p-[3px]"
            />
            <TextInput
              value={material.emissiveColor}
              onChange={(e) => {
                const val = e.target.value
                if (val.match(/^#[0-9A-Fa-f]{0,8}$/)) handleChange('emissiveColor', val)
              }}
              onBlur={(e) => {
                const val = e.target.value
                if (!val.match(/^#[0-9A-Fa-f]{6}([0-9A-Fa-f]{2})?$/)) handleChange('emissiveColor', '#000000')
              }}
              placeholder="#000000ff"
              className="font-mono uppercase"
            />
          </div>
        </Field>
        {range('Strength', 'emissiveIntensity', 0, 5, 0.1)}
      </Section>

      <Section title="Rendering">
        <Field label="Render side" value={material.side}>
          <Select value={material.side} onChange={(e) => handleChange('side', e.target.value as MaterialSide)}>
            {sideOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Select>
        </Field>
        <Checkbox
          checked={material.showUvTestText}
          onChange={(v) => handleChange('showUvTestText', v)}
          label="UV test — show text"
          className="mb-xs"
        />
        <Checkbox
          checked={material.showUvTestPattern}
          onChange={(v) => handleChange('showUvTestPattern', v)}
          label="UV test — show checkerboard"
        />
        <p className="text-text-xs text-gray-400 mt-xs">Verify UV mapping with text and/or a checkerboard.</p>
      </Section>
    </div>
  )
}
